import { useCallback, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { t } from '@lingui/macro';
import * as yup from 'yup';

import { Modal } from '@decisiv/ui-components';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';
import Pencil from '@decisiv/iconix/lib/components/Pencil';

import { createForm, FormHandler } from 'components/Form';
import { isStateValidForCountry } from 'utils/countryRegions';
import useUpdateManagedPlatformAccountMutation from 'hooks/useUpdateManagedPlatformAccountMutation';

import Content from './Content';

// Helper function to decompose phone number
const decomposePhoneNumber = (
  phone: string | undefined,
): { countryCode: string; phoneNumber: string } => {
  if (!phone) return { countryCode: '+1', phoneNumber: '' };

  const countryCodes = ['+1', '+52', '+56', '+593', '+81'];
  const matchedCode = countryCodes.find((code) => phone.startsWith(code));

  if (matchedCode) {
    return {
      countryCode: matchedCode,
      phoneNumber: phone.slice(matchedCode.length),
    };
  }

  return { countryCode: '+1', phoneNumber: phone };
};

const validationSchema = yup
  .object({
    name: yup.string().required(t`Name is required`),
    city: yup.string().required(t`City is required`),
    state: yup
      .string()
      .required(t`State is required`)
      .test(
        'state-country-match',
        t`Select a state that belongs to the chosen country`,
        function stateMatchesSelectedCountry(value) {
          const { country } = (this.parent as { country?: string }) || {};

          if (!country || !value) return true;

          return isStateValidForCountry(value, country);
        },
      ),
    address1: yup.string().required(t`Address Line 1 is required`),
    address2: yup.string(),
    postalCode: yup.string().required(t`Postal Code is required`),
    country: yup.string().required(t`Country is required`),
    countryCode: yup.string(),
    phoneNumber: yup.string(),
    email: yup.string().email(t`Enter a valid email address.`),
  })
  .test(
    'contact-info',
    t`Provide at least an email address or phone number.`,
    (values) => {
      const { phoneNumber, email } = values;
      return !!(phoneNumber || email);
    },
  )
  .required();

type FormData = yup.InferType<typeof validationSchema>;

const { Form, FormProvider } = createForm<FormData>();

interface EditAccountModalProps {
  onClose: () => void;
  platformAccount: {
    id: string;
    name: string;
    city?: string;
    state?: string;
    address1?: string;
    address2?: string;
    postalCode?: string;
    country?: string;
    phone?: string;
    email?: string;
  };
}

const EditAccountModal = ({
  onClose,
  platformAccount,
}: EditAccountModalProps) => {
  const {
    formState: { isValid },
  } = useFormContext();
  const { notify } = useNotifications();

  const formRef = useRef<FormHandler<FormData>>(null);

  const handleTriggerForm = useCallback(async () => {
    await formRef.current?.submit();
  }, []);

  const [updateManagedPlatformAccount, { loading: updating }] =
    useUpdateManagedPlatformAccountMutation({
      refetchQueries: ['ManagedPlatformAccounts'],
      awaitRefetchQueries: true,
    });

  const handleOnSubmit = useCallback(
    async (formData: FormData) => {
      try {
        // Compose phone number: countryCode + sanitized phoneNumber (remove all non-digits)
        const sanitizedPhone = formData.phoneNumber
          ? formData.phoneNumber.replace(/\D/g, '')
          : '';
        const fullPhone = sanitizedPhone
          ? `${formData.countryCode}${sanitizedPhone}`
          : '';

        await updateManagedPlatformAccount({
          variables: {
            platformAccountId: platformAccount.id,
            attributes: {
              name: formData.name,
              city: formData.city,
              state: formData.state,
              address1: formData.address1,
              address2: formData.address2 || '',
              postalCode: formData.postalCode,
              country: formData.country,
              phone: fullPhone,
              email: formData.email,
            },
          },
        });

        notify({
          duration: 5000,
          intent: 'success',
          title: t`Fleet account "${formData.name}" updated successfully`,
          onClose: () => null,
        } as any);

        onClose();
      } catch (error) {
        notify({
          duration: 5000,
          intent: 'danger',
          title: t`Failed to update fleet account`,
          onClose: () => null,
        } as any);
      }
    },
    [updateManagedPlatformAccount, platformAccount.id, notify, onClose],
  );

  const modalActions = useMemo(
    () =>
      [
        {
          text: t`Save`,
          loading: updating,
          disabled: !isValid,
          onClick: handleTriggerForm,
        },
        {
          text: t`Cancel`,
          onClick: onClose,
        },
      ] as [any, any],
    [updating, isValid, handleTriggerForm, onClose],
  );

  return (
    <Modal
      actions={modalActions}
      title={t`Edit Fleet`}
      intent="information"
      icon={Pencil}
      color="information"
      visible
      onClose={onClose}
    >
      <Form ref={formRef} onSubmit={handleOnSubmit}>
        <Content platformAccountId={platformAccount.id} />
      </Form>
    </Modal>
  );
};

interface EditAccountModalWrapperProps {
  visible: boolean;
  onClose: () => void;
  platformAccount: {
    id: string;
    name: string;
    city?: string;
    state?: string;
    address1?: string;
    address2?: string;
    postalCode?: string;
    country?: string;
    phone?: string;
    email?: string;
  };
}

const EditAccountModalWrapper = ({
  visible,
  onClose,
  platformAccount,
}: EditAccountModalWrapperProps) => {
  if (!visible) return null;

  const { countryCode, phoneNumber } = decomposePhoneNumber(
    platformAccount.phone,
  );

  const initialValues: FormData = useMemo(
    () => ({
      name: platformAccount.name || '',
      city: platformAccount.city || '',
      state: platformAccount.state || '',
      address1: platformAccount.address1 || '',
      address2: platformAccount.address2 || '',
      postalCode: platformAccount.postalCode || '',
      country: platformAccount.country || 'US',
      countryCode,
      phoneNumber,
      email: platformAccount.email || '',
    }),
    [
      platformAccount.name,
      platformAccount.city,
      platformAccount.state,
      platformAccount.address1,
      platformAccount.address2,
      platformAccount.postalCode,
      platformAccount.country,
      platformAccount.email,
      countryCode,
      phoneNumber,
    ],
  );

  return (
    <FormProvider
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      <EditAccountModal onClose={onClose} platformAccount={platformAccount} />
    </FormProvider>
  );
};

export default EditAccountModalWrapper;
